import React from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  IconButton,
  Button,
  Checkbox,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { cn } from '@/lib/utils'
import classes from '*.module.css'

export interface AccordionCardBreadcrumb {
  name: string
  id?: string
}

export interface AccordionCardAttribute {
  label: string
  value: string | React.ReactNode
}

export interface AccordionCardAction {
  label: string
  variant?: 'contained' | 'outlined' | 'text'
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  onClick: () => void
}

export interface AccordionCardProps {
  id: string
  title: string
  breadcrumbs?: AccordionCardBreadcrumb[]
  attributes?: AccordionCardAttribute[]
  actions?: AccordionCardAction[]
  content?: React.ReactNode
  expanded?: boolean
  selected?: boolean
  onToggle?: () => void
  onSelect?: (checked: boolean) => void
  showCheckbox?: boolean
  className?: string
}

const AccordionCard: React.FC<AccordionCardProps> = ({
  id,
  title,
  breadcrumbs = [],
  attributes = [],
  actions = [],
  content,
  expanded = false,
  selected = false,
  onToggle,
  onSelect,
  showCheckbox = true,
  className,
}) => {
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (onSelect) {
      onSelect(event.target.checked)
    }
  }

  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      className={className}
      sx={{
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '0 !important',
        },
        boxShadow: 'none',
        border: 'none',
        margin: 0,
      }}
    >
      <AccordionSummary
        sx={{
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '0 !important',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            display: 'none',
          },
          padding: 0,
          minHeight: 'auto !important',
          '&.Mui-expanded': {
            minHeight: 'auto !important',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className="tw-px-4"
          sx={{ width: '100%' }}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            {showCheckbox && (
              <Checkbox
                checked={selected}
                onChange={handleCheckboxChange}
                onClick={handleCheckboxClick}
                size="small"
              />
            )}
            <Stack sx={{ py: '12px' }}>
              <Typography
                component="p"
                sx={{ fontSize: '14px', fontWeight: 600 }}
              >
                {title}
              </Typography>
              {breadcrumbs.length > 0 && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#5F5E5E',
                  }}
                >
                  {breadcrumbs.map(b => b.name).join('/')}
                </Typography>
              )}
            </Stack>
          </Stack>
          <IconButton size="small">
            <ChevronRightIcon
              color="primary"
              className={cn('tw-transition-transform', {
                'tw-rotate-90': expanded,
              })}
            />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 0, pb: 0 }}>
        <div className="card-body">
          {content}

          {actions.length > 0 && (
            <div className="tw-flex tw-justify-end tw-gap-3 tw-px-4 tw-pt-4">
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'outlined'}
                  color={action.color || 'primary'}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  )
}

export default AccordionCard
